import React, { useState, useEffect } from 'react';
import { Card, Button, Badge, Row, Col, Spinner, Alert, Form, Modal, Image, Container } from 'react-bootstrap';
import { FaHeart, FaComment, FaEye, FaFlag, FaReply, FaArrowLeft, FaThumbsUp } from 'react-icons/fa';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import api from '../../../api/apiClient';
import { getAssetUrl } from '../../../utils/apiBase';
import { useAuth } from '../../../contexts/AuthContext';
import { decodeForumId } from '../../../utils/urlEncryption';

const OrgForumDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [forum, setForum] = useState(null);
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [commentsLoading, setCommentsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [showCommentModal, setShowCommentModal] = useState(false);
  const [commentForm, setCommentForm] = useState({
    content: '',
    attachment: null
  });
  const [submitting, setSubmitting] = useState(false);
  const [likedForums, setLikedForums] = useState(new Set());
  const [likedComments, setLikedComments] = useState(new Set());

  useEffect(() => {
    fetchForum();
    fetchComments();
  }, [id]);

  const fetchForum = async () => {
    try {
      setLoading(true);
      setError(null);
      
      let decodedId = decodeForumId(id);
      if (!decodedId) {
        const numericId = parseInt(id);
        if (!isNaN(numericId)) {
          decodedId = numericId;
        } else {
          setError('Invalid forum ID');
          return;
        }
      }
      
      const response = await api.forums.getById(decodedId);
      
      if (response.data.success) {
        setForum(response.data.data);
      } else {
        setError(response.data.message || 'Failed to fetch forum');
      }
    } catch (error) {
      console.error('Error fetching forum:', error);
      setError('Failed to fetch forum. Please try again.');
      toast.error('Failed to fetch forum');
    } finally {
      setLoading(false);
    }
  };

  const fetchComments = async () => {
    try {
      setCommentsLoading(true);
      
      let decodedId = decodeForumId(id);
      if (!decodedId) {
        const numericId = parseInt(id);
        if (!isNaN(numericId)) {
          decodedId = numericId;
        } else {
          toast.error('Invalid forum ID');
          return;
        }
      }
      
      const response = await api.forums.getComments(decodedId, {
        page: currentPage,
        limit: 20
      });
      
      if (response.data.success) {
        setComments(response.data.data.comments);
        setTotalPages(response.data.data.totalPages);
      } else {
        toast.error(response.data.message || 'Failed to fetch comments');
      }
    } catch (error) {
      console.error('Error fetching comments:', error);
      toast.error('Failed to fetch comments');
    } finally {
      setCommentsLoading(false);
    }
  };

  const handleLikeForum = async () => {
    try {
      let decodedId = decodeForumId(id);
      if (!decodedId) {
        const numericId = parseInt(id);
        if (!isNaN(numericId)) {
          decodedId = numericId;
        } else {
          return;
        }
      }
      
      const response = await api.forums.toggleLike(decodedId);
      
      if (response.data.success) {
        setForum(prev => ({
          ...prev,
          likes_count: response.data.likesCount
        }));
        
        if (response.data.liked) {
          setLikedForums(prev => new Set([...prev, forum.id]));
        } else {
          setLikedForums(prev => {
            const newSet = new Set(prev);
            newSet.delete(forum.id);
            return newSet;
          });
        }
      }
    } catch (error) {
      console.error('Error toggling like:', error);
      toast.error('Failed to toggle like');
    }
  };

  const handleLikeComment = async (commentId) => {
    try {
      const response = await api.forums.toggleCommentLike(commentId);
      
      if (response.data.success) {
        setComments(prev => prev.map(comment => 
          comment.id === commentId 
            ? { ...comment, likes_count: response.data.likesCount }
            : comment
        ));
        
        if (response.data.liked) {
          setLikedComments(prev => new Set([...prev, commentId]));
        } else {
          setLikedComments(prev => {
            const newSet = new Set(prev);
            newSet.delete(commentId);
            return newSet;
          });
        }
      }
    } catch (error) {
      console.error('Error toggling comment like:', error);
      toast.error('Failed to toggle comment like');
    }
  };

  const handleHighlightComment = async (commentId) => {
    try {
      const response = await api.forums.highlightComment(commentId, 'Inappropriate content');
      
      if (response.data.success) {
        toast.success('Comment highlighted for moderation');
        fetchComments();
      } else {
        toast.error(response.data.message || 'Failed to highlight comment');
      }
    } catch (error) {
      console.error('Error highlighting comment:', error);
      toast.error('Failed to highlight comment');
    }
  };

  const handleSubmitComment = async (e) => {
    e.preventDefault();
    
    if (!commentForm.content.trim()) {
      toast.error('Please enter a comment');
      return;
    }

    try {
      setSubmitting(true);
      let decodedId = decodeForumId(id);
      if (!decodedId) {
        const numericId = parseInt(id);
        if (!isNaN(numericId)) {
          decodedId = numericId;
        } else {
          toast.error('Invalid forum ID');
          return;
        }
      }
      const response = await api.forums.createComment(decodedId, commentForm);
      
      if (response.data.success) {
        toast.success('Comment added successfully');
        setShowCommentModal(false);
        setCommentForm({ content: '', attachment: null });
        fetchComments();
      } else {
        toast.error(response.data.message || 'Failed to add comment');
      }
    } catch (error) {
      console.error('Error adding comment:', error);
      toast.error('Failed to add comment');
    } finally {
      setSubmitting(false);
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setCommentForm(prev => ({ ...prev, attachment: file }));
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const canHighlightComment = () => {
    return user.role === 1 || user.role === 2; // Users and Org Admins can highlight
  };

  if (loading) {
    return (
      <div className="p-3 sm:p-4 md:p-6 bg-gradient-to-br from-purple-50 via-pink-50 to-blue-50 min-h-screen">
        <div className="flex justify-center items-center" style={{ height: '400px' }}>
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-purple-600 border-t-transparent"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-3 sm:p-4 md:p-6 bg-gradient-to-br from-purple-50 via-pink-50 to-blue-50 min-h-screen">
        <div className="mb-3 p-3 rounded bg-red-50 border border-red-100 text-red-700">{error}</div>
        <Button
          bsPrefix="custom-hamburger"
          onClick={() => navigate('/organization/forums')}
          className="border-1 border-purple-600 bg-white text-purple-600 hover:from-purple-700 hover:to-pink-700 font-semibold rounded-lg sm:rounded-xl shadow-md hover:shadow-lg transition-all duration-200 text-xs sm:text-sm flex items-center justify-center gap-2 p-2"
        >
          <FaArrowLeft className="w-4 h-4" />
          Back to Forums
        </Button>
      </div>
    );
  }

  if (!forum) {
    return (
      <div className="p-3 sm:p-4 md:p-6 bg-gradient-to-br from-purple-50 via-pink-50 to-blue-50 min-h-screen">
        <div className="mb-3 p-3 rounded bg-yellow-50 border border-yellow-100 text-yellow-700">Forum not found</div>
        <Button
          bsPrefix="custom-hamburger"
          onClick={() => navigate('/organization/forums')}
          className="border-1 border-purple-600 bg-white text-purple-600 hover:from-purple-700 hover:to-pink-700 font-semibold rounded-lg sm:rounded-xl shadow-md hover:shadow-lg transition-all duration-200 text-xs sm:text-sm flex items-center justify-center gap-2 p-2"
        >
          <FaArrowLeft className="w-4 h-4" />
          Back to Forums
        </Button>
      </div>
    );
  }

  return (
    <div className="p-3 sm:p-4 md:p-6 bg-gradient-to-br from-purple-50 via-pink-50 to-blue-50 min-h-screen">
      {/* Enhanced Header matching Admin Panel */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-4 gap-3">
        <div className="flex items-center gap-3">
          <Button
            bsPrefix="custom-hamburger"
            onClick={() => navigate('/organization/forums')}
            className="flex items-center gap-2 px-4 py-2 bg-white border border-purple-300 text-purple-700 rounded-lg shadow-md hover:bg-purple-50 hover:shadow-lg transition-all duration-200 text-sm font-medium"
          >
            <FaArrowLeft className="w-4 h-4" />
            Back
          </Button>
          <h5 className="fw-bold mb-0 text-xl sm:text-2xl md:text-3xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-purple-600 to-pink-600">
            Forum Discussion
          </h5>
        </div>
      </div>

      <Row>
        <Col lg={12}>
          {/* Enhanced Forum Content Card */}
          <Card className="mb-4 shadow-lg rounded-xl border-0 overflow-hidden">
            <Card.Header className="bg-white border-bottom">
              <div className="d-flex justify-content-between align-items-start">
                <div className="flex-grow-1">
                  <h3 className="mb-2 fw-bold text-gray-800" style={{ lineHeight: '1.3' }}>
                    {forum.title}
                  </h3>
                  <div className="d-flex align-items-center gap-3 mb-2 flex-wrap">
                    <span className="text-gray-600 text-sm">
                      by <strong>{forum.creator?.first_name} {forum.creator?.last_name}</strong>
                    </span>
                    <span className={`px-3 py-1 rounded-full text-xs font-bold text-white shadow-md ${forum.org_id ? 'bg-blue-600' : 'bg-green-600'}`}>
                      {forum.org_id ? 'Organization' : 'Global'}
                    </span>
                    <span className="text-gray-600 text-sm">{formatDate(forum.createdAt)}</span>
                  </div>
                </div>
              </div>
            </Card.Header>
            
            {forum.attachment && (
              <Card.Img
                variant="top"
                src={getAssetUrl(forum.attachment)}
                style={{ maxHeight: '400px', objectFit: 'cover' }}
                onError={(e) => {
                  e.target.style.display = 'none';
                }}
              />
            )}
            <Card.Body>
              <Card.Text className="mb-4" style={{ whiteSpace: 'pre-wrap' }}>
                {forum.description}
              </Card.Text>
            </Card.Body>
            <Card.Footer className="bg-light py-2">
              <div className="d-flex align-items-center gap-3 flex-wrap">
                 {/* Like Button - Compact */}
                 <a
                   href="#"
                   rel="noopener noreferrer"
                   onClick={(e) => {
                     e.preventDefault();
                     handleLikeForum();
                   }}
                   className="d-inline-flex align-items-center px-2"
                   style={{ width: 'auto', textDecoration: 'none', color: 'red' }}
                 >
                   <FaHeart className="me-1" style={{ color: 'red' }} />
                   {forum.likes_count > 0 && (
                     <span style={{ color: 'red' }}>{forum.likes_count} Likes</span>
                   )}
                 </a>

                {/* Comment Count */}
                <div className="text-muted d-flex align-items-center">
                  <FaComment className="me-1" />
                  <small>{comments.length > 0 ? `${comments.length} Comments` : 'No Comments'}</small>
                </div>

                {/* View Count */}
                <div className="text-muted d-flex align-items-center">
                  <FaEye className="me-1" />
                  <small>{forum.views_count || 0} Views</small>
                </div>
              </div>
            </Card.Footer>
          </Card>

          {/* Comments Section */}
          <Card className="shadow-lg rounded-xl border-0 overflow-hidden">
            <Card.Header className="bg-gradient-to-r from-purple-600 to-pink-600 text-white">
              <div className="d-flex justify-content-between align-items-center">
                <h5 className="mb-0 font-bold text-white">
                  {comments.length > 0 ? `Comments (${comments.length})` : 'Comments'}
                </h5>
                <Button
                  bsPrefix="custom-hamburger"
                  onClick={() => setShowCommentModal(true)}
                  className="border-1 border-white bg-white/20 text-white hover:bg-white/30 font-semibold rounded-lg sm:rounded-xl shadow-md hover:shadow-lg transition-all duration-200 text-xs sm:text-sm flex items-center justify-center gap-2 p-2"
                >
                  <FaReply className="w-4 h-4" />
                  Add Comment
                </Button>
              </div>
            </Card.Header>
            <Card.Body className="bg-gray-50">
              {commentsLoading ? (
                <div className="text-center py-4">
                  <Spinner animation="border" size="sm" />
                  <span className="ms-2">Loading comments...</span>
                </div>
              ) : comments.length === 0 ? (
                <div className="text-center py-4 text-muted">
                  <FaComment size={32} className="mb-2" />
                  <p>No comments yet. Be the first to comment!</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {comments.map(comment => (
                    <div key={comment.id} className="border-bottom pb-3 mb-3">
                      <div className="d-flex justify-content-between align-items-start mb-2">
                        <div className="d-flex align-items-center">
                          <div className="bg-primary text-white rounded-circle d-flex align-items-center justify-content-center me-2" 
                               style={{ width: '32px', height: '32px', fontSize: '14px' }}>
                            {comment.user?.first_name?.[0]}{comment.user?.last_name?.[0]}
                          </div>
                          <div>
                            <strong className="d-block">
                              {comment.user?.first_name} {comment.user?.last_name}
                            </strong>
                            <small className="text-muted">
                              {formatDate(comment.createdAt)}
                            </small>
                          </div>
                        </div>
                        {comment.is_highlighted !==0 && (
                          <Badge bg="warning" className="d-flex align-items-center">
                            <FaFlag className="me-1" />
                            Flagged
                          </Badge>
                        )}
                      </div>
                      
                      <div className="ms-4">
                        <p className="mb-2" style={{ whiteSpace: 'pre-wrap' }}>
                          {comment.content}
                        </p>
                        
                        {comment.attachment && (
                          <div className="mb-2">
                            <Image
                              src={getAssetUrl(comment.attachment)}
                              alt="Comment attachment"
                              style={{ maxWidth: '200px', maxHeight: '200px', objectFit: 'cover' }}
                              className="rounded"
                              onError={(e) => {
                                e.target.style.display = 'none';
                              }}
                            />
                          </div>
                        )}
                        
                        <div className="d-flex gap-2 align-items-center justify-content-end">
                          <Button
                            bsPrefix="custom-hamburger"
                            variant={likedComments.has(comment.id) ? 'primary' : 'outline-primary'}
                            size="sm"
                            onClick={() => handleLikeComment(comment.id)}
                            className="border-0 p-1 d-flex align-items-center"
                            title="Like comment"
                          >
                            <FaThumbsUp className="w-4 h-4" />
                            {comment.likes_count > 0 && (
                              <span className="ms-1">{comment.likes_count}</span>
                            )}
                          </Button>
                          
                          {canHighlightComment() && !comment.is_highlighted && (
                            <Button
                              bsPrefix="custom-hamburger"
                              variant="outline-warning"
                              size="sm"
                              onClick={() => handleHighlightComment(comment.id)}
                              className="border-0 p-1 d-flex align-items-center"
                              title="Flag as Vulgar"
                            >
                              <FaFlag className="w-4 h-4" />
                            </Button>
                          )}
                          {comment.is_highlighted ===1 && (
                            <Badge bg="warning" className="d-flex align-items-center px-2 py-1">
                              <FaFlag className="w-3 h-3 me-1" />
                              <span className="text-xs">Flagged</span>
                            </Badge>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Add Comment Modal */}
      <Modal show={showCommentModal} onHide={() => setShowCommentModal(false)} size="lg" centered>
        <Modal.Header closeButton closeVariant="white" className="bg-gradient-to-r from-purple-600 to-pink-600 text-white border-0 py-3 px-4 sm:px-4 sm:py-4 modal-header">
          <Modal.Title className="font-bold text-xl text-white">Add Comment</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSubmitComment}>
          <Modal.Body className="bg-gray-50 p-3 sm:p-5">
            <div className="mb-4">
              <label className="block text-sm font-semibold text-gray-700 mb-1">Comment <span className="text-red-500">*</span></label>
              <textarea
                rows={4}
                value={commentForm.content}
                onChange={(e) => setCommentForm(prev => ({ ...prev, content: e.target.value }))}
                placeholder="Write your comment here..."
                required
                className="w-full px-3 py-2 rounded-lg border border-purple-200 focus:ring-2 focus:ring-purple-500 outline-none text-sm"
              />
            </div>
            <div className="mb-6">
              <label className="block text-sm font-semibold text-gray-700 mb-1">Attachment (Optional)</label>
              <input
                type="file"
                onChange={handleFileChange}
                accept="image/*,.pdf,.doc,.docx"
                className="w-full px-3 py-2 rounded-lg border border-purple-200 focus:ring-2 focus:ring-purple-500 outline-none text-sm"
              />
              <p className="text-xs text-gray-500 mt-1">Supported formats: Images, PDF, DOC, DOCX (Max 5MB)</p>
            </div>

            <Modal.Footer className="bg-gray-100 border-0 rounded-b-2xl p-4">
              <Row className="w-100 g-3">
                <Col md={6}>
                  <Button bsPrefix="custom-hamburger" onClick={() => setShowCommentModal(false)} disabled={submitting} className="w-full border-1 bg-gray-100 hover:bg-gray-200 text-gray-700 border-2 border-gray-300 hover:border-gray-500 font-semibold rounded-lg sm:rounded-xl shadow-md hover:shadow-lg transition-all duration-200 text-xs sm:text-sm h-full flex items-center justify-center gap-2 p-2">
                    Cancel
                  </Button>
                </Col>
                <Col md={6}>
                  <Button
                    bsPrefix="custom-hamburger"
                    type="submit"
                    disabled={submitting}
                    className="w-full border-1 border-purple-600 bg-white text-purple-600 hover:from-purple-700 hover:to-pink-700 font-semibold rounded-lg sm:rounded-xl shadow-md hover:shadow-lg transition-all duration-200 text-xs sm:text-sm h-full flex items-center justify-center gap-2 p-2"
                  >
                    {submitting ? (
                      <>
                        <Spinner size="sm" className="me-2" />
                        Posting...
                      </>
                    ) : (
                      'Post Comment'
                    )}
                  </Button>
                </Col>
              </Row>
            </Modal.Footer>
          </Modal.Body>
        </Form>
      </Modal>
    </div>
  );
};

export default OrgForumDetail;
